/**
 * documentation domain (doc 01 A5.2, Task 21 W3/W5) — the worker's "notes
 * still to write" queue plus the org-wide documentation records surface for
 * admins. Read-only. Types mirror the API's `documentation/schemas.ts`
 * contract verbatim (mark for consolidation).
 *
 * The worker sees their OWN delivered-but-undocumented service events: client
 * initials (never a full name), the service date, and how long outstanding.
 * The admin records surface lists every delivered visit in the tenant with its
 * documentation state (coalesced to 'undocumented' when no status row exists),
 * and the detail view adds the visit's own facts — including the note itself.
 * The backend returns the authoritative rows; this client never fabricates
 * rows or an "all caught up" state.
 *
 * IT WAS READ-ONLY, AND THAT MADE ONE OF ITS OWN READS A CONSTANT.
 *
 * `POST /services/events/:id/documentation/transition` and
 * `PUT /services/events/:id/documentation` have been routed, guarded and
 * audited on the server for as long as this module has existed, with an
 * authority model (`documentationAuthorityFor`) that carefully separates the
 * worker writing their own note from the reviewer judging somebody else's.
 * Nothing on this side called either one. So `state` only ever moved for a
 * worker draining their queue from the phone — `apps/worker-mobile` issues the
 * PUT itself, with raw fetch — and the REVIEWER half of that model was
 * unreachable from any screen: 'late' and 'void' are reviewer-only states, and
 * `isLate` / `lagDays` / `incompleteCount` are reviewer-only fields.
 *
 * `is_late` is a STORED input, not a derived one — the semantics layer says so
 * in as many words (`intelligence/semantics/vocabulary.ts`: "a stored boolean
 * supplied by whoever ... only as complete as the source system"). With no
 * client able to supply it, nothing in a fresh tenant ever set it, and the
 * shipped `docs_overdue` KPI — `docs.filter((d) => d.is_late).length` in
 * `dashboard/widgetDataService.ts` — reads a permanent 0. An organization looks
 * at "Documentation overdue: 0" and reads a clean compliance record, when what
 * it is actually seeing is a column nobody could write. A zero produced by an
 * unreachable input is not the same fact as a zero produced by nothing being
 * late, and only one of them is true.
 *
 * The two writes live under `/services/events/:id/...` because a documentation
 * status hangs off a delivered visit, but they are documentation acts and this
 * is where a caller looks for them. Both are guarded and audited server-side.
 */
import { get, post, put } from './transport.js';
/** `GET /documentation/to-write` → `{ items: ToWriteItem[] }` (needs `documentation:read`). */
export async function toWrite(options) {
    const body = await get('/documentation/to-write', options);
    return body.items;
}
/**
 * `GET /documentation` — the tenant's documentation records, newest visit
 * first (needs `documentation:read`).
 */
export async function list(query = {}, options) {
    const q = {
        state: query.state,
        isLate: query.isLate === undefined ? undefined : String(query.isLate),
        workerId: query.workerId,
        clientId: query.clientId,
        limit: query.limit,
        offset: query.offset,
    };
    return get('/documentation', { ...options, query: q });
}
/**
 * `GET /documentation/:serviceEventId` — one record in full (needs
 * `documentation:read`). Resolves 404 when the record is not in the caller's
 * tenant — the backend never distinguishes a foreign id from a missing one.
 */
export async function getDocumentation(serviceEventId, options) {
    const body = await get(`/documentation/${encodeURIComponent(serviceEventId)}`, options);
    return body.documentation;
}
/**
 * `GET /documentation/:serviceEventId/narrative-revisions` — every recorded
 * earlier version of the visit's narrative, oldest first, plus the live text.
 *
 * Needs `documentation:read` and the SAME scope as the detail view above: an
 * out-of-scope or foreign record resolves 404, never 403, because a 403 would
 * confirm the record exists. Reading this is audited server-side exactly as
 * reading the note is, so do not call it to prefetch.
 */
export async function narrativeRevisions(serviceEventId, options) {
    return get(`/documentation/${encodeURIComponent(serviceEventId)}/narrative-revisions`, options);
}
/**
 * `POST /services/events/:serviceEventId/documentation/transition` →
 * `{ documentationStatus }`.
 *
 * MOVES an existing documentation status to a new state, and is the verb a
 * review screen wants: the server records the transition and emits
 * `documentation.transitioned`, so the change is visible as an event and not
 * only as a new value. Prefer it over `setDocumentationState` for any change to
 * a record that already has a status.
 *
 * Needs `service:manage` OR `service:write` to reach the surface at all, and
 * then the authority over THIS record decides what the body may contain: a
 * reviewer may send any legal state and the compliance scalars; the note's own
 * author may send 'draft' or 'complete' and nothing else. A `service:manage`
 * holder is the author, not the reviewer, of their own note — the reviewer
 * authority stops at the reviewer's own record.
 *
 * Refusals to expect, and they mean different things: 403 for a state or a
 * scalar this caller may not set on this record (the message names them), 404
 * when the visit is not in the caller's tenant or scope.
 */
export async function transition(serviceEventId, input, options) {
    const body = await post(`/services/events/${encodeURIComponent(serviceEventId)}/documentation/transition`, input, options);
    return body.documentationStatus;
}
/**
 * `PUT /services/events/:serviceEventId/documentation` →
 * `{ documentationStatus }`.
 *
 * SETS the status, creating the row when the visit has none yet — the write for
 * a visit whose status does not exist, where `transition` has nothing to move.
 * Same authority rules and the same refusals.
 *
 * A self-service PUT that omits the compliance scalars does NOT clear them: the
 * server preserves the reviewer's judgement across a worker's save rather than
 * resetting it to the schema defaults. Do not rely on omission to unset
 * anything.
 */
export async function setState(serviceEventId, input, options) {
    const body = await put(`/services/events/${encodeURIComponent(serviceEventId)}/documentation`, input, options);
    return body.documentationStatus;
}
/**
 * `GET /services/events/:serviceEventId/documentation` →
 * `{ documentationStatus }`.
 *
 * The status row alone, for a screen about to write one. NULL when the visit has
 * no status row yet, which is an ANSWER — "nobody has started this note" — and
 * not an error; `list` above coalesces that same absence to 'undocumented'
 * because a list needs a value in every cell, but here the distinction is
 * available and worth keeping. A caller that renders null as 'undocumented' is
 * agreeing with the list; a caller that renders it as an empty draft is not.
 *
 * Needs `service:read` plus documentation scope on the subject. Returns the
 * documentation FACTS only — never the narrative, which is disclosed by exactly
 * one audited surface (`getDocumentation` above).
 */
export async function status(serviceEventId, options) {
    const body = await get(`/services/events/${encodeURIComponent(serviceEventId)}/documentation`, options);
    return body.documentationStatus ?? null;
}
/**
 * `GET /documentation/completeness` — documentation status as metadata.
 *
 * Needs `documentation:read_metadata`; widened to the organization by
 * `documentation:read_metadata_all`. A caller holding `documentation:read` and
 * `documentation:read_all` and neither metadata key is refused with 403 — which
 * a screen must render as a refusal, never as an organization with nothing
 * outstanding.
 *
 * NOT AUDITED, unlike `getDocumentation`: this returns states, timestamps and
 * UUIDs rather than a clinical record about a named person. That is a reason to
 * prefer it wherever the question is "how much is outstanding", and not a
 * licence to poll it in place of the audited read when the question is "what
 * does the note say".
 */
export async function completeness(query = {}, options) {
    const q = {
        workerId: query.workerId,
        clientId: query.clientId,
        limit: query.limit,
        offset: query.offset,
    };
    return get('/documentation/completeness', {
        ...options,
        query: q,
    });
}
//# sourceMappingURL=documentation.js.map